import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { toApiDate, todayInWarsaw } from 'core/utils/zoomTime';
import { getFontFamily } from 'services/FontService';

type PresetKey = 'd2' | 'd7' | 'm1' | 'm2' | 'm3';

/**
 * Quick relative date-range presets for the Zoom pages — all anchored on
 * Europe/Warsaw "today". Drives the same `from`/`to` as the manual
 * {@link ZoomDateRangeFilter} so the two are interchangeable.
 */
const PRESETS: { key: PresetKey; from: () => DateTime }[] = [
  { key: 'd2', from: () => todayInWarsaw().minus({ days: 1 }) },
  { key: 'd7', from: () => todayInWarsaw().minus({ days: 6 }) },
  { key: 'm1', from: () => todayInWarsaw().minus({ months: 1 }) },
  { key: 'm2', from: () => todayInWarsaw().minus({ months: 2 }) },
  { key: 'm3', from: () => todayInWarsaw().minus({ months: 3 }) },
];

interface IZoomRangePresetsProps {
  value: [DateTime, DateTime] | null;
  onChange: (from: DateTime, to: DateTime) => void;
}

export const ZoomRangePresets = ({
  value,
  onChange,
}: IZoomRangePresetsProps) => {
  const { t } = useTranslation();

  const activeKey: PresetKey | null = (() => {
    if (!value) {
      return null;
    }
    const today = todayInWarsaw();
    if (toApiDate(value[1]) !== toApiDate(today)) {
      return null;
    }
    const fromApi = toApiDate(value[0]);
    return PRESETS.find((p) => toApiDate(p.from()) === fromApi)?.key ?? null;
  })();

  return (
    <Bar>
      {PRESETS.map((preset) => (
        <PresetButton
          key={preset.key}
          type="button"
          $active={activeKey === preset.key}
          onClick={() => onChange(preset.from(), todayInWarsaw())}
        >
          {t(`pages.zoom.presets.${preset.key}`)}
        </PresetButton>
      ))}
    </Bar>
  );
};

const Bar = styled.div`
  display: inline-flex;
  gap: 4px;
`;

const PresetButton = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  cursor: pointer;
  font-family: ${({ $active }) => getFontFamily($active ? 'bold' : 'regular')};
  font-size: 13px;
  line-height: 20px;
  color: ${({ $active }) => ($active ? '#ffffff' : '#374151')};
  background: ${({ $active }) => ($active ? '#2563eb' : '#ffffff')};
  border: 1px solid ${({ $active }) => ($active ? '#2563eb' : '#d1d5db')};
  border-radius: 6px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #2563eb;
    color: ${({ $active }) => ($active ? '#ffffff' : '#2563eb')};
  }
`;
